/**
 * liveMapStatus
 *
 * One controller's live map, reduced to the three answers somebody standing in
 * front of it actually wants: what is watering right now, how much longer it
 * has, and what comes next.
 *
 * The map tab used to answer none of those without work. A zone that was open
 * differed from a zone that was not by fill opacity, its remaining time lived
 * behind a click on the polygon, and "Today's Schedule" listed every sequence
 * of the day with the current one highlighted somewhere in the middle. All the
 * information was on the page and none of it was legible at a glance.
 *
 * THE TIMING RULE THIS EXISTS TO GET RIGHT
 *   A step's relays run simultaneously but each carries its own
 *   `duration_sec`, so `step.end_sec` is when the STEP finishes, not when this
 *   valve closes. Timing a valve off the step keeps a two-minute run on screen
 *   for the length of a twenty-minute step — and the map did exactly that. The
 *   windows here come from `relayWindows`, the same helper the irrigation
 *   landing page times its run cards from, so the two pages cannot disagree
 *   about whether a valve is open.
 *
 * Everything is compared in the LOCAL day frame (see `localDaySec`): the
 * timeline's seconds are UTC but the programs in it were selected by today's
 * LOCAL weekday, and comparing the two raw reads a +10 site's whole morning as
 * already past. Callers fold `nowSec` and pass the offset; nothing here
 * formats a clock time from a folded value — upcoming runs carry an epoch
 * instant instead, derived from their own countdown.
 */

import {
  relayWindows,
  VALVE_REPORT_GRACE_SEC,
  type TimelineWindow,
} from './irrigationRuns';
import {
  getRelayState,
  relayCountFor,
  relayDisplayName,
  type EspNowRouter,
  type LiveMapData,
  type RelayZone,
} from '../services/espnowIrrigation';

/**
 * What one valve is doing.
 *
 * `running` and `due` are deliberately different claims. `running` is the
 * node's own relay bitmask saying the valve is open. `due` is a window that is
 * open with nothing heard from the node since it opened — worth showing, and
 * not worth drawing as water on the ground. A node that HAS reported since the
 * window opened with the valve still shut is `did-not-start`: that is a dry
 * block, and it belongs in an alert rather than on a run card.
 */
export type ValveState =
  | 'running'
  | 'manual'
  | 'due'
  | 'did-not-start'
  | 'skipped'
  | 'upcoming'
  | 'finished'
  | 'idle';

export interface ValveStatus {
  /** `${router_mac}:${relay_index}` — the key every zone lookup already uses. */
  key: string;
  routerMac: string;
  routerName: string;
  relayIndex: number;
  zoneName: string;
  zoneColor: string;
  /** True once somebody has drawn the ground this valve waters. */
  mapped: boolean;
  state: ValveState;
  /** This valve's own run length, not its step's. Null when nothing times it. */
  durationSec: number | null;
  elapsedSec: number | null;
  remainingSec: number | null;
  /** 0..1 through the current window, or null when nothing times it. */
  progress: number | null;
  startsInSec: number | null;
  /** Epoch ms the next run is due. Derived from the countdown, never from a
   *  folded seconds-of-day value — see the module note. */
  startsAtMs: number | null;
  endedAgoSec: number | null;
  skippedAgoSec: number | null;
  /** The nodes that did not answer, when the sequence was skipped. */
  silentNodes: string[];
}

export interface LiveMapStatus {
  byKey: Map<string, ValveStatus>;
  /** Bitmask says open — the headline count. Includes manual runs. */
  watering: ValveStatus[];
  /** Window open, nothing heard from the node since it opened. */
  due: ValveStatus[];
  /** Window open and the node reports the valve shut. A block is dry. */
  failed: ValveStatus[];
  /** Sequences the bridge skipped, still recent enough to be worth saying. */
  skipped: ValveStatus[];
  /** Everything still to come today, earliest first. */
  upcoming: ValveStatus[];
  /** True when today's programs put at least one window on at least one valve. */
  hasWindows: boolean;
}

/** The most recent skip that touched this valve, if any. */
function lastSkip(
  timeline: LiveMapData['timeline'],
  routerMac: string,
  relayIndex: number,
  nowSec: number,
  fold: (sec: number) => number
): { startSec: number; silentNodes: string[] } | null {
  let found: { startSec: number; silentNodes: string[] } | null = null;
  for (const step of timeline) {
    if (!step.skipped) continue;
    if (!step.relays.some((r) => r.router_mac === routerMac && r.relay_index === relayIndex)) {
      continue;
    }
    const startSec = fold(step.start_sec);
    if (startSec > nowSec) continue;
    if (!found || startSec > found.startSec) {
      found = { startSec, silentNodes: step.silent_nodes ?? [] };
    }
  }
  return found;
}

export interface BuildLiveMapStatusArgs {
  liveData: LiveMapData;
  /** Router rows from the parent page — refreshed more often than the map poll,
   *  so relay bitmasks and `relay_state_updated_at` are freshest here. */
  routers: EspNowRouter[];
  /** The controller's link type — the relay-count default for XBee nodes. */
  transport: string | null;
  /** Now, already folded into the local day by the caller. */
  nowSec: number;
  /** The site's UTC offset in seconds, for folding window starts to match. */
  offsetSec: number;
  nowMs: number;
}

export function buildLiveMapStatus({
  liveData,
  routers,
  transport,
  nowSec,
  offsetSec,
  nowMs,
}: BuildLiveMapStatusArgs): LiveMapStatus {
  const fold = (sec: number) => (((sec + offsetSec) % 86400) + 86400) % 86400;

  const zoneByKey = new Map<string, RelayZone>();
  for (const z of liveData.zones) zoneByKey.set(`${z.router_mac}:${z.relay_index}`, z);

  // The live-map payload carries its own router rows, but the parent page polls
  // routers on a shorter cycle. Prefer the fresher one per MAC and fall back,
  // so a node that only the map knows about still gets a row.
  const routerByMac = new Map<string, EspNowRouter>();
  for (const r of liveData.routers) routerByMac.set(r.router_mac, r);
  for (const r of routers) routerByMac.set(r.router_mac, r);

  const byKey = new Map<string, ValveStatus>();
  const watering: ValveStatus[] = [];
  const due: ValveStatus[] = [];
  const failed: ValveStatus[] = [];
  const skipped: ValveStatus[] = [];
  const upcoming: ValveStatus[] = [];
  let hasWindows = false;

  for (const router of routerByMac.values()) {
    // relayCountFor, not usableRelays: a relay marked out of use that still
    // carries a schedule row will still be driven, and hiding a valve that is
    // actually open is the worse failure.
    const relayCount = relayCountFor(router, transport);
    for (let relayIndex = 0; relayIndex < relayCount; relayIndex++) {
      const key = `${router.router_mac}:${relayIndex}`;
      const zone = zoneByKey.get(key);
      const windows: TimelineWindow[] = relayWindows(
        liveData.timeline,
        router.router_mac,
        relayIndex,
        offsetSec
      );
      if (windows.length > 0) hasWindows = true;

      const on = getRelayState(router.relay_states, relayIndex);
      const active =
        windows.find((w) => nowSec >= w.startSec && nowSec < w.startSec + w.durationSec) ?? null;
      const next = windows.find((w) => w.startSec > nowSec) ?? null;
      const finished = [...windows]
        .reverse()
        .find((w) => w.startSec + w.durationSec <= nowSec) ?? null;
      const skip = lastSkip(liveData.timeline, router.router_mac, relayIndex, nowSec, fold);

      // A valve nobody has mapped, scheduled or switched is not worth a row.
      if (!on && !active && !next && !finished && !skip && !zone) continue;

      const base: ValveStatus = {
        key,
        routerMac: router.router_mac,
        routerName: router.name || router.router_mac.slice(-6),
        relayIndex,
        zoneName: relayDisplayName(
          zone?.zone_name,
          router.relay_labels,
          relayIndex,
          `Relay ${relayIndex + 1}`
        ),
        zoneColor: zone?.zone_color || '#3B82F6',
        mapped: !!zone?.boundary_geometry,
        state: 'idle',
        durationSec: null,
        elapsedSec: null,
        remainingSec: null,
        progress: null,
        startsInSec: null,
        startsAtMs: null,
        endedAgoSec: null,
        skippedAgoSec: null,
        silentNodes: [],
      };

      let status: ValveStatus;

      if (on && active) {
        const elapsedSec = nowSec - active.startSec;
        status = {
          ...base,
          state: 'running',
          durationSec: active.durationSec,
          elapsedSec,
          remainingSec: active.durationSec - elapsedSec,
          progress: Math.min(1, elapsedSec / active.durationSec),
        };
        watering.push(status);
      } else if (on) {
        // Open with no window to explain it. Nothing is going to close it on a
        // timer, and saying "remaining" here would be a guess.
        status = { ...base, state: 'manual' };
        watering.push(status);
      } else if (active) {
        const elapsedSec = nowSec - active.startSec;
        const windowStartMs = nowMs - elapsedSec * 1000;
        const reportedAtMs = router.relay_state_updated_at
          ? Date.parse(router.relay_state_updated_at)
          : NaN;
        // Only a report made INSIDE the window can testify that the valve is
        // shut. An older bitmask is silence, not evidence — which is the whole
        // reason `relay_state_updated_at` is carried.
        const toldSinceStart =
          Number.isFinite(reportedAtMs) &&
          reportedAtMs >= windowStartMs + VALVE_REPORT_GRACE_SEC * 1000;
        status = {
          ...base,
          state: toldSinceStart ? 'did-not-start' : 'due',
          durationSec: active.durationSec,
          elapsedSec,
          remainingSec: active.durationSec - elapsedSec,
          progress: Math.min(1, elapsedSec / active.durationSec),
        };
        (toldSinceStart ? failed : due).push(status);
      } else if (skip && (!finished || skip.startSec >= finished.startSec + finished.durationSec)) {
        status = {
          ...base,
          state: 'skipped',
          skippedAgoSec: nowSec - skip.startSec,
          silentNodes: skip.silentNodes,
        };
        skipped.push(status);
      } else if (next) {
        const startsInSec = next.startSec - nowSec;
        status = {
          ...base,
          state: 'upcoming',
          durationSec: next.durationSec,
          startsInSec,
          startsAtMs: nowMs + startsInSec * 1000,
        };
        upcoming.push(status);
      } else if (finished) {
        status = {
          ...base,
          state: 'finished',
          durationSec: finished.durationSec,
          endedAgoSec: nowSec - (finished.startSec + finished.durationSec),
        };
      } else {
        status = base;
      }

      byKey.set(key, status);
    }
  }

  const byRemaining = (a: ValveStatus, b: ValveStatus) =>
    (a.remainingSec ?? Number.MAX_SAFE_INTEGER) - (b.remainingSec ?? Number.MAX_SAFE_INTEGER);

  watering.sort(byRemaining);
  due.sort(byRemaining);
  upcoming.sort((a, b) => (a.startsInSec ?? 0) - (b.startsInSec ?? 0));

  return { byKey, watering, due, failed, skipped, upcoming, hasWindows };
}
